import React, { useState, useEffect, useMemo } from 'react';
import { Download, Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import PaystackPop from '@paystack/inline-js';
import PDFThumbnail from '../components/PDFThumbnail';
import StatusModal from '../components/StatusModal';
import API_BASE_URL from '../config';

const ITEMS_PER_PAGE = 6;

const PresentationCard = React.memo(({ presentation, getDisplayPrice, handlePurchase, isDownloading }) => {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 flex flex-col h-full">
      <div className="relative aspect-video bg-gray-200">
        <PDFThumbnail presentationId={presentation.id} title={presentation.title} />
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <div className="flex-grow">
          <h3 className="text-xl font-bold mb-2 text-gray-800 line-clamp-1">
            {presentation.title}
          </h3>
          <p className="text-gray-600 mb-4 text-sm line-clamp-2">
            {presentation.description}
          </p>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar size={14} className="mr-1" />
              {presentation.date}
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-500 mb-6 font-medium">
            {presentation.category}
          </div>
        </div>

        <div className="mb-4 text-center font-bold text-amber-600 text-xl">
          {getDisplayPrice()}
        </div>

        <div className="mt-auto">
          <button
            onClick={() => handlePurchase(presentation)}
            disabled={isDownloading}
            className={`w-full bg-amber-500 hover:bg-amber-600 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-all duration-300 ${isDownloading ? 'opacity-75 cursor-not-allowed' : 'transform hover:scale-105 active:scale-95 shadow-md'
              }`}
          >
            <Download size={18} className="mr-2" />
            {isDownloading ? "Processing..." : 'Buy & Download'}
          </button>
        </div>
      </div>
    </div>
  );
});

const Presentations = () => {
  const [filter, setFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [userCurrency, setUserCurrency] = useState('NGN');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const showStatus = (type, title, message) => {
    setStatusModal({ isOpen: true, type, title, message });
  };

  useEffect(() => {
    const detectLocation = async () => {
      try {
        // Call our OWN backend (never blocked by browsers)
        const response = await fetch(`${API_BASE_URL}/api/detect-location`);
        const data = await response.json();
        // ip-api.com returns 'countryCode'
        if (data.countryCode && data.countryCode !== 'NG') {
          setUserCurrency('USD');
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      }
    };
    detectLocation();
  }, []);

  const PRICE_NGN = 10000;
  const PRICE_USD = 7;

  const getDisplayPrice = React.useCallback(() => {
    return userCurrency === 'NGN' ? `₦${PRICE_NGN.toLocaleString()}` : `$${PRICE_USD}`;
  }, [userCurrency]);

  const presentations = React.useMemo(() => [
    {
      id: 1,
      title: "LMDS ANNUAL RETURNS PRESENTATION",
      description: "Case of Individual Assesstment - Filing Individual Annual Returns (IAR)- Agenda",
      date: "December 10, 2024",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 2,
      title: "TAX MANAGEMENT AND COMPLIANCE",
      description: "TAX MANAGEMENT AND COMPLIANCE FOR MICRO, SMALL AND MEDIUM ENTERPRISES MSMESs",
      date: "August 2023",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 3,
      title: "TAX PLANNING PROCESSES - PRACTICAL APPLICATION",
      description: "Tax planning is the analysis of a financial situation or plan to ensure that all elements work together to allow...",
      date: "June, 2023",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 4,
      title: "TAX FILING  AND RECORD MANAGEMENT",
      description: "BEST PRACTICES IN ONLINE ARCHIVING & PHYSICAL FILING OF DOCUMENTS...",
      date: "June 2023",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 5,
      title: "CHALLENGES OF MULTIPLE TAXATION IN NIGERIA WAY FORWARD",
      description: "First, are the various unlawful compulsory payments being collected by the local and state governments...",
      date: "November 2023",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 6,
      title: "TAX MANAGEMENT AND COMPLIANCE FOR MSMEs",
      description: "The significance of micro, small, and medium scale enterprises for the growth...",
      date: "April 2023",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 7,
      title: "BUILDING A SUSTAINABLE BUSINESS BRAND – GOVT. PERSPECTIVE",
      description: "The five key elements of a sustainable business model...",
      date: "June 2024",
      isPremium: true,
      category: "Governance"
    },
    {
      id: 8,
      title: "IMPLEMENTATION OF THE GLOBAL MINIMUM TAX AND ITS IMPLICATION FOR BUSINESSES IN NIGERIA",
      description: "In response to the Great Recession, unprecedented fiscal, monetary, and regulatory policies...",
      date: "Nov 2024",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 9,
      title: "Empower Her: Breaking Barriers, Building Futures",
      description: "EXPLORING THE CONNECTION: INCLUSIVE GROWTH, GENDER BALANCE, AND THE CONSEQUENCES OF EXCLUSION...",
      date: "March 2024",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 10,
      title: "Implementation of the Global Minimum Tax and Its Implication for Businesses in Nigeria",
      description: "The digital economy is made up of economic activities conducted or facilitated through digital technologies...",
      date: "April 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 11,
      title: "Implementation of the Global Minimum Tax and Its Implication for Businesses in Nigeria - NBC",
      description: "The digital economy is made up of economic activities conducted or facilitated through digital technologies...",
      date: "August 2024",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 12,
      title: "THE CHARTERED ACCOUNTANT AND GLOBAL VISIBILITY",
      description: "The word “accountant”, Brings what to mind...",
      date: "October 2024",
      isPremium: true,
      category: "Accounting"
    },
    {
      id: 13,
      title: "TAX PLANNING STRATEGIES FOR INDIVIDULAS AND BUSINESSES",
      description: "Tax planning is a proactive financial practice that helps...",
      date: "February 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 14,
      title: "COMPREHENSIVE TRAINING FOR PAYE/PIT STAFF",
      description: "COMPREHENSIVE TRAINING FOR PAYE/PIT STAFF ON ENUMERATION PROCESS AND JOB RESPONSIBILITIES...",
      date: "March 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 15,
      title: "AIRS 2025 REVENUE RETREAT - LEGAL PROCEDURES",
      description: "Engage in thorough research on various tax issues and other areas that concern the Agency as a whole...",
      date: "March 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 16,
      title: "Rudiments of Withholding Tax: Filing Procedures & Practical Application ",
      description: "Withholding taxes (WHT) are when tax is withheld from (or deducted from the income due to...",
      date: "March 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 17,
      title: "IMAGE AND SOCIAL MEDIA MANAGEMENT",
      description: "Monitor your overall body carriage and corporate/social presence...",
      date: "February 2025",
      isPremium: true,
      category: "Media"
    },
    {
      id: 18,
      title: "The role of Democracy in Developing Nations: Opportunities, Challenges and Contributions of Young Chartered Accountants",
      description: "Democracy's heart lies in inclusivity and agency. It emphasizes each person's perspective and...",
      date: "March 2025",
      isPremium: true,
      category: "Governance"
    },
    {
      id: 19,
      title: "The Macroeconomics Trends and Development in 2026: Impact Analysis on the Nigerian Leasing Industry",
      description: "Nigeria’s Gross Domestic Product (GDP) expanded by 3.98 per cent year-on-year in Q3 2025, outperforming the IMF’s full-year projection...",
      date: "March 2026",
      isPremium: true,
      category: "Finance"
    },
    {
      id: 20,
      title: "Emerging Trends in The Nigerian Tax Space",
      description: "he previous WHT Regulations exempted “sales in the ordinary course of business” from WHT. However, this exemption was...",
      date: "August 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 21,
      title: "Fiscal Policy Interventions and Its Impact on Businesses in Nigeria",
      description: "Fiscal policy refers to the use of government spending (investment on social and economic overheads ...",
      date: "April 2025",
      isPremium: true,
      category: "Policy"
    },
    {
      id: 22,
      title: "HARNESSING ARTIFICIAL INTELLIGENCE (AI) FOR TAX ADMINISTRATION AND COMPLIANCE IN NIGERIA",
      description: "AI refers to machines that can mimic human cognitive functions like reasoning, perception, and language understanding...",
      date: "October 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 23,
      title: "Equipping graduates for the leap of learning to leading",
      description: "Led2Lead Consulting Limited is a forward-thinking consulting firm committed to empowering individuals...",
      date: "September 2025",
      isPremium: true,
      category: "Mentorship"
    },
    {
      id: 24,
      title: "WHY FINANCIAL POWER IS IMPORTATNT FOR WOMEN",
      description: "THE 2026 BLUEPRINT FOR SAVING, INVESTING AND MULTIPLYING MONEY...",
      date: "November 2025",
      isPremium: true,
      category: "Advocacy"
    },
    {
      id: 25,
      title: "HOW MENTORSHIP HAS CHANGED LIVES, KEY TAKEAWAYS",
      description: "Mentors help individuals identify and leverage their strengths, and without this guidance,...",
      date: "November 2025",
      isPremium: true,
      category: "Mentorship"
    },
    {
      id: 26,
      title: "Show up with confidence: The Modern Empowered Woman Who stands Out, Inspires Her World, and Shines Bright",
      description: "Strategies for Professional Women to Boost Influence, Drive Impact, and Elevate their Leadership Presence...",
      date: "December 2025",
      isPremium: true,
      category: "Advocacy"
    },
    {
      id: 27,
      title: "TAXATION FOR STARTUPS AND SMEs: WHAT ENTREPRENEURS NEEDS TO KNOW",
      description: "Within six months of commencement register with a tax authority for individual taxes and company...",
      date: "May 2025",
      isPremium: true,
      category: "Tax"
    },
    {
      id: 28,
      title: "TAXATION OF THE INFORMAL SECTOR - OPPORTUNITIES, CHALLENGES AND REWARDS",
      description: "The Informal Sector comprises of any economic activity or source of income that is not fully registered,...",
      date: "March 2025",
      isPremium: true,
      category: "Tax"
    },
  ], []);

  const categories = useMemo(() => {
    const counts = presentations.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {});

    const uniqueCategories = [...new Set(presentations.map(p => p.category))];

    uniqueCategories.sort((a, b) => {
      if (a === 'Tax') return -1;
      if (b === 'Tax') return 1;
      return counts[b] - counts[a];
    });

    return ["All", ...uniqueCategories];
  }, [presentations]);

  const filteredPresentations = React.useMemo(() =>
    filter === 'All'
      ? presentations
      : presentations.filter(p => p.category === filter)
    , [filter, presentations]);

  const totalPages = Math.ceil(filteredPresentations.length / ITEMS_PER_PAGE);
  const paginatedPresentations = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPresentations.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPresentations, currentPage]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Smooth scroll to grid top
    const element = document.getElementById('presentations');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Trigger payment flow
  const handlePurchase = (presentation) => {
    setSelectedPresentation(presentation);
    setShowModal(true);
  };

  // Secure fulfilment via backend streaming
  const initiateSecureDownload = (id, reference) => {
    const downloadUrl = `${API_BASE_URL}/api/stream-presentation/${id}?reference=${reference}`;
    window.location.href = downloadUrl;
  };

  // Function to handle payment and then download
  const handlePaymentAndDownload = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address for your receipt.');
      return;
    }
    setEmailError('');
    setIsDownloading(true);

    try {
      // Convert $7 or ₦10,000 to base currency units
      const amount = userCurrency === 'NGN' ? PRICE_NGN * 100 : PRICE_USD * 100;

      // 1. Call backend to initialize Paystack payment
      const initRes = await fetch(`${API_BASE_URL}/api/initiate-presentation-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          amount,
          currency: userCurrency,
          presentationId: selectedPresentation.id,
          itemPrice: userCurrency === 'NGN' ? PRICE_NGN : PRICE_USD,
          deliveryFee: 0
        }),
      });

      const initData = await initRes.json();
      if (!initData.access_code) {
        throw new Error("Failed to get access code");
      }

      // 2. Launch Paystack modal
      const paystack = new PaystackPop();
      paystack.newTransaction({
        accessCode: initData.access_code,
        onSuccess: async (transaction) => {
          try {
            // 3. Verify payment with backend
            const verifyRes = await fetch(`${API_BASE_URL}/api/verify-payment`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ reference: transaction.reference }),
            });

            const verifyData = await verifyRes.json();

            if (verifyData.success) {
              // 4. Trigger Secure Download
              initiateSecureDownload(selectedPresentation.id, transaction.reference);

              // 5. Close modal and reset
              setShowModal(false);
              setSelectedPresentation(null);
            } else {
              showStatus("error", "Verification Failed", "Payment could not be verified. Please contact support.");
            }
          } catch (verifyError) {
            console.error("Verification error:", verifyError);
            showStatus("error", "Error", "Something went wrong verifying the payment.");
          } finally {
            setIsDownloading(false);
          }
        },
        onCancel: () => {
          showStatus("error", "Payment Cancelled", "The payment process was cancelled.");
          setIsDownloading(false);
        },
      });
    } catch (error) {
      console.error("Payment initialization error:", error);
      showStatus("error", "Error", "Something went wrong initializing the payment.");
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Enhanced Hero Section with proper spacing for navbar */}
      <div className="relative bg-gradient-to-r from-amber-600 to-amber-500 pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-white"></div>
          <div className="absolute bottom-0 left-1/2 w-48 h-48 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Engaging Presentations, Valuable Insights
            </h1>
            <p className="text-xl md:text-2xl text-white opacity-90 mb-10 max-w-3xl mx-auto">
              Explore a collection of powerful presentations designed to inform, inspire, and transform. Access free resources or purchase exclusive content to elevate your knowledge.
            </p>

            {/* Animated Scroll Indicator */}
            <div className="flex justify-center">
              <a
                href="#presentations"
                className="flex flex-col items-center text-white opacity-80 hover:opacity-100 transition-opacity duration-300"
              >
                <span className="text-sm mb-2">Explore Collection</span>
                <ChevronDown className="animate-bounce" size={24} />
              </a>
            </div>
          </div>
        </div>

        {/* Wave Shape Divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-16 md:h-24"
          >
            <path
              fill="#F9FAFB"
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C0,0,31,12,94.56,30.29,155.85,47.84,242.24,69.69,321.39,56.44Z"
            ></path>
          </svg>
        </div>
      </div>

      <div id="presentations" className="container mx-auto py-16 px-6">
        {/* Category Filter Tags */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12 px-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${filter === category
                ? 'bg-amber-500 text-white border-amber-500 shadow-md transform scale-105'
                : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-600'
                }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Presentations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {paginatedPresentations.map(presentation => (
            <PresentationCard
              key={presentation.id}
              presentation={presentation}
              getDisplayPrice={getDisplayPrice}
              handlePurchase={handlePurchase}
              isDownloading={isDownloading}
            />
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-full transition-all ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-amber-500 hover:bg-amber-50'}`}
              aria-label="Previous Page"
            >
              <ChevronLeft size={24} />
            </button>

            <div className="flex flex-wrap justify-center gap-1">
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 md:w-10 md:h-10 rounded-full font-medium transition-all text-sm md:text-base ${currentPage === pageNum ? 'bg-amber-500 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-full transition-all ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-amber-500 hover:bg-amber-50'}`}
              aria-label="Next Page"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}

        {/* No Results */}
        {filteredPresentations.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              No presentations found for this filter.
            </p>
          </div>
        )}

      </div>

      {/* Payment Modal (Optional/Cleanup) */}
      {showModal && selectedPresentation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 m-4 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h3 className="text-xl font-bold mb-4">
              {selectedPresentation.title}
            </h3>
            <p className="mb-6 text-gray-600">
              Your presentation will download immediately after successful payment.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address (For payment receipt)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 outline-none ${emailError ? 'border-red-500' : 'border-gray-300'}`}
              />
              {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
            </div>

            <div className="border rounded-lg p-4 mb-6 bg-gray-50 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Price:</span>
                <span className="font-bold text-amber-600 text-lg">
                  {getDisplayPrice()}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Format:</span>
                <span>PDF</span>
              </div>
            </div>

            <button
              onClick={handlePaymentAndDownload}
              disabled={isDownloading}
              className={`w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center transition-all ${isDownloading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
            >
              {isDownloading ? "Processing..." : `Pay ${getDisplayPrice()} & Download`}
            </button>
          </div>
        </div>
      )}
      {/* Status Modal for Success/Error messages */}
      <StatusModal
        {...statusModal}
        onClose={() => setStatusModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default Presentations;